import { useResizeObserver } from '@folklore/hooks';
import { getSizeWithinBounds } from '@folklore/size';
import classNames from 'classnames';
import { isString } from 'lodash';
import React, { useMemo } from 'react';

import useFullscreen from '../../hooks/useFullscreen';
import { isIos } from '../../lib/utils';

import { useContest } from '../../contexts/ContestContext';
import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';
import FullscreenIcon from '../icons/Fullscreen';
import OtherLocales from '../partials/OtherLocales';

import styles from '../../../styles/layouts/display.module.scss';

interface RatioLayoutProps {
    page?: Page;
    pageLoading?: boolean;
    withFullscreen?: boolean;
    withLocale?: boolean;
    children?: React.ReactNode;
}

function RatioLayout({
    page: currentPage = null,
    pageLoading = false,
    withFullscreen = true,
    withLocale = true,
    children = null,
}: RatioLayoutProps) {
    const { urls: pageUrls = null } = currentPage || {};
    // const url = useUrlGenerator();
    // const hasContest = contest !== null;
    const { locales = null } = useContest();
    const hasLocales = locales !== null && locales.length > 1;

    const theme = useTheme();
    const themeClassName = useThemeClassName(styles, true);
    const { name: themeName = null, backgroundColor = null } = theme || {};

    const iOS = isIos();

    const {
        ref: resizeRef,
        entry: { contentRect = null },
    } = useResizeObserver();

    const { toggle: toggleFullscreen, active: fullscreenActive } = useFullscreen(
        resizeRef.current || null,
    );

    const { width: containerWidth, height: containerHeight } = contentRect || {};

    const finalBgColor = isString(backgroundColor) ? [backgroundColor] : backgroundColor;
    const background = useMemo(
        () =>
            finalBgColor !== null && themeName !== 'nfl'
                ? `${
                      finalBgColor.length === 1
                          ? finalBgColor[0]
                          : `${finalBgColor[0]} linear-gradient(${finalBgColor.join(',')})`
                  }`
                : undefined,
        [finalBgColor],
    );

    const isHorizontal = containerWidth > containerHeight;
    const screenWidth = isHorizontal ? 1920 : 1080;
    const screenHeight = isHorizontal ? 1080 : 1920;

    const { scale: screenScale } = getSizeWithinBounds(
        screenWidth,
        screenHeight,
        containerWidth,
        containerHeight,
    );

    const { handle, type } = currentPage || {};

    return (
        <div
            className={classNames([
                styles.container,
                `layout-page-${handle || type}`,
                {
                    [styles.pageLoading]: pageLoading,
                    [styles.fullscreen]: fullscreenActive,
                },
                themeClassName,
            ])}
            style={{ background }}
            ref={resizeRef}
        >
            <main
                className={styles.fullscreen}
                style={{
                    width: screenWidth,
                    height: screenHeight,
                    transform: `translate(-50%, -50%) scale(${screenScale}) `,
                }}
            >
                {children}
            </main>
            {withFullscreen && !iOS ? (
                <StyledButton onClick={toggleFullscreen} className={styles.fullscreenButton}>
                    {/* <FormattedMessage defaultMessage="Fullscreen" description="button label" /> */}
                    <FullscreenIcon />
                </StyledButton>
            ) : null}
            {withLocale && hasLocales ? (
                <OtherLocales className={styles.localeButton} pageUrls={pageUrls} withButton />
            ) : null}
        </div>
    );
}

export default RatioLayout;
